import { useState } from 'react';
import { cashApi, receivablesApi } from '../../api/services';
import { AR_STATUS, DOC_TYPES } from '../../api/types';
import type { CreateReceivableDocumentDto, CustomerDto, ReceivableApplicationDto, ReceivableDocumentDto, ReceivablePaymentDto, SaveCustomerDto } from '../../api/types';
import {
  Alert, Badge, Button, Card, Check, Field, Form, Input, Loadable, Modal, NumberInput, PageHeader, Pager, Select, Table, Tabs, Toolbar,
  fmtDate, fmtMoney, money, round2, todayIso, useAction, useAsync,
} from '../../ui/components';
import { BankAccountSelect, CurrencySelect, TemplateSelect, useCurrencies } from '../../ui/pickers';
import { AgingView, StatementView } from '../shared/AgingViews';
import { PageHelp } from '../../ui/help';
import { idTypeOptions, useCountry } from '../../ui/company';

type Tab = 'documents' | 'payments' | 'customers' | 'aging' | 'statement';
type DocType = 'FAC' | 'ND' | 'NC';

export function ReceivablesPage() {
  const [tab, setTab] = useState<Tab>('documents');
  return (
    <>
      <PageHeader title="Cuentas por cobrar" subtitle="Submayor contable propio. La facturación envía sus documentos y cobros por la API de integración; también pueden registrarse aquí." />
      <PageHelp page="receivables" />
      <Tabs<Tab> tabs={[{ key: 'documents', label: 'Documentos' }, { key: 'payments', label: 'Cobros' }, { key: 'customers', label: 'Clientes' }, { key: 'aging', label: 'Antigüedad' }, { key: 'statement', label: 'Estado de cuenta' }]} active={tab} onChange={setTab} />
      {tab === 'documents' && <DocumentsTab />}
      {tab === 'payments' && <PaymentsTab />}
      {tab === 'customers' && <CustomersTab />}
      {tab === 'aging' && <AgingView load={(asOf) => receivablesApi.aging(asOf)} csvReport="receivables-aging" />}
      {tab === 'statement' && <StatementTab />}
    </>
  );
}

const statusTone = (s: string): 'neutral' | 'ok' | 'warn' | 'bad' => (s === 'ANU' ? 'bad' : s === 'CAN' || s === 'APL' ? 'ok' : 'warn');

/** Buscador de clientes de CxC (CXC_CLIENTE de la empresa). */
function CustomerSearch({ value, onChange }: { value: CustomerDto | null; onChange: (c: CustomerDto | null) => void }) {
  const [text, setText] = useState('');
  const res = useAsync(() => receivablesApi.customers({ text: text.trim(), status: 'A', page: 1, pageSize: 10 }), [text], text.trim().length >= 2);
  if (value) return <span className="acc-row"><strong>{value.code}</strong> {value.name} <Button kind="link" onClick={() => onChange(null)}>Cambiar</Button></span>;
  return (
    <div className="acc-search">
      <Input value={text} onChange={setText} placeholder="Código, nombre o identificación (mín. 2 letras)" />
      {res.data && res.data.items.length > 0 && (
        <ul className="acc-search-results">
          {res.data.items.map((c) => <li key={c.id}><button type="button" onClick={() => onChange(c)}>{c.code} · {c.name} <span className="acc-muted">saldo {fmtMoney(c.balanceBase)}</span></button></li>)}
        </ul>
      )}
    </div>
  );
}

function currencyLabel(currencies: { id: number; name: string; iso?: string | null }[] | undefined, id: number) {
  const c = currencies?.find((x) => x.id === id);
  return c ? (c.iso ?? c.name) : String(id);
}

// ================================================================ Documentos

function DocumentsTab() {
  const [f, setF] = useState({ type: '', status: '', number: '', onlyPending: true, onlyOverdue: false, page: 1 });
  const list = useAsync(() => receivablesApi.documents({ type: f.type || undefined, status: f.status || undefined, number: f.number || undefined, onlyPending: f.onlyPending, onlyOverdue: f.onlyOverdue, page: f.page, pageSize: 50 }), [f]);
  const currencies = useCurrencies();
  const [creating, setCreating] = useState<DocType | null>(null);
  const [detail, setDetail] = useState<number | null>(null);
  return (
    <>
      <Toolbar>
        <Select value={f.type} onChange={(v) => setF({ ...f, type: v, page: 1 })} placeholder="Todos los tipos" options={Object.entries(DOC_TYPES).map(([value, label]) => ({ value, label }))} />
        <Select value={f.status} onChange={(v) => setF({ ...f, status: v, page: 1 })} placeholder="Todos los estados" options={['PEN', 'CAN', 'ANU'].map((value) => ({ value, label: AR_STATUS[value] }))} />
        <Input value={f.number} onChange={(v) => setF({ ...f, number: v, page: 1 })} placeholder="Número" />
        <Check checked={f.onlyPending} onChange={(v) => setF({ ...f, onlyPending: v, page: 1 })} label="Sólo pendientes" />
        <Check checked={f.onlyOverdue} onChange={(v) => setF({ ...f, onlyOverdue: v, page: 1 })} label="Sólo vencidos" />
        <Button kind="primary" onClick={() => setCreating('FAC')}>Factura</Button>
        <Button onClick={() => setCreating('ND')}>Nota de débito</Button>
        <Button onClick={() => setCreating('NC')}>Nota de crédito</Button>
      </Toolbar>
      <Loadable state={list}>
        {(r) => (
          <>
            <Table rows={r.items} rowKey={(d) => d.id} onRowClick={(d) => setDetail(d.id)} columns={[
              { key: 't', title: 'Tipo', render: (d) => d.type },
              { key: 'n', title: 'Número', render: (d) => d.number },
              { key: 'c', title: 'Cliente', render: (d) => `${d.customerCode}${d.customerName ? ` · ${d.customerName}` : ''}` },
              { key: 'f', title: 'Fecha', render: (d) => fmtDate(d.date) },
              { key: 'v', title: 'Vence', render: (d) => fmtDate(d.dueDate) },
              { key: 'm', title: 'Moneda', render: (d) => currencyLabel(currencies.data, d.currencyId) },
              money<ReceivableDocumentDto>('to', 'Total', (d) => d.total),
              money<ReceivableDocumentDto>('s', 'Saldo', (d) => d.balance),
              { key: 'e', title: 'Estado', render: (d) => <Badge tone={d.daysOverdue > 0 ? 'bad' : statusTone(d.status)}>{AR_STATUS[d.status] ?? d.status}{d.daysOverdue > 0 ? ` · ${d.daysOverdue} d` : ''}</Badge> },
              { key: 'o', title: 'Origen', render: (d) => d.origin },
              { key: 'a', title: 'Asiento', render: (d) => d.entryCode ?? '' },
            ]} />
            <Pager page={r.page} totalPages={r.totalPages} total={r.total} onPage={(p) => setF({ ...f, page: p })} />
          </>
        )}
      </Loadable>
      {creating && <DocumentForm type={creating} onClose={() => setCreating(null)} onSaved={() => { setCreating(null); list.reload(); }} />}
      {detail !== null && <DocumentDetail id={detail} onClose={() => setDetail(null)} onChanged={list.reload} />}
    </>
  );
}

function DocumentForm({ type, onClose, onSaved }: { type: DocType; onClose: () => void; onSaved: () => void }) {
  const [customer, setCustomer] = useState<CustomerDto | null>(null);
  const [v, setV] = useState<CreateReceivableDocumentDto>({ customerCode: '', number: '', date: todayIso(), dueDate: null, currencyId: 0, exchangeRate: null, subtotal: 0, discount: 0, tax: 0, exempt: 0, taxed: 0, otherCharges: 0, total: 0, description: '', referenceDocumentId: null, templateCode: null });
  const refs = useAsync(() => (customer && type !== 'FAC' ? receivablesApi.documents({ customerId: customer.id, onlyPending: false, pageSize: 200 }) : Promise.resolve(null)), [customer?.id, type]);
  const { run, busy, fieldErrors } = useAction();
  const reference = (refs.data?.items ?? []).filter((d) => d.type !== 'NC' && d.status !== 'ANU');
  return (
    <Modal title={`Registrar ${DOC_TYPES[type].toLowerCase()}`} onClose={onClose} size="lg">
      <Form onSubmit={() => run(async () => { await receivablesApi.createDocument(type, { ...v, customerCode: customer!.code, number: v.number.trim(), description: v.description?.trim() || null }); onSaved(); }, { success: `${DOC_TYPES[type]} registrada con su asiento.` })}>
        <Field label="Cliente" wide><CustomerSearch value={customer} onChange={(c) => { setCustomer(c); setV({ ...v, referenceDocumentId: null }); }} /></Field>
        <div className="acc-grid-form">
          <Field label="Número" error={fieldErrors.Number}><Input value={v.number} onChange={(x) => setV({ ...v, number: x })} maxLength={50} required /></Field>
          <Field label="Fecha"><Input type="date" value={v.date} onChange={(x) => setV({ ...v, date: x })} required /></Field>
          {type !== 'NC' && <Field label="Vencimiento" hint="Vacío = fecha + días de crédito del cliente"><Input type="date" value={v.dueDate ?? ''} onChange={(x) => setV({ ...v, dueDate: x || null })} /></Field>}
          <Field label="Moneda"><CurrencySelect value={v.currencyId || null} onChange={(x) => setV({ ...v, currencyId: x })} /></Field>
          <Field label="Tipo de cambio" hint="Vacío = tipo de cambio del día (o el del documento de referencia)"><NumberInput step="0.000001" value={v.exchangeRate} onChange={(x) => setV({ ...v, exchangeRate: x || null })} /></Field>
          <Field label="Subtotal"><NumberInput value={v.subtotal} onChange={(x) => setV({ ...v, subtotal: x })} min={0} /></Field>
          <Field label="Descuento"><NumberInput value={v.discount} onChange={(x) => setV({ ...v, discount: x })} min={0} /></Field>
          <Field label="Impuesto"><NumberInput value={v.tax} onChange={(x) => setV({ ...v, tax: x })} min={0} /></Field>
          <Field label="Otros cargos"><NumberInput value={v.otherCharges} onChange={(x) => setV({ ...v, otherCharges: x })} min={0} /></Field>
          <Field label="Exento"><NumberInput value={v.exempt} onChange={(x) => setV({ ...v, exempt: x })} min={0} /></Field>
          <Field label="Gravado"><NumberInput value={v.taxed} onChange={(x) => setV({ ...v, taxed: x })} min={0} /></Field>
          <Field label="Total" error={fieldErrors.Total}><NumberInput value={v.total} onChange={(x) => setV({ ...v, total: x })} min={0} /></Field>
          {type !== 'FAC' && (
            <Field label="Documento de referencia" hint={type === 'NC' ? 'La nota se aplica automáticamente a ese documento' : undefined}>
              <Select value={v.referenceDocumentId ?? ''} onChange={(x) => setV({ ...v, referenceDocumentId: x || null })} placeholder="(ninguno)"
                options={reference.map((d) => ({ value: d.id, label: `${d.type} ${d.number} · saldo ${fmtMoney(d.balance)}` }))} />
            </Field>
          )}
          <Field label="Plantilla"><TemplateSelect moduleCode="CxC" value={v.templateCode} onChange={(x) => setV({ ...v, templateCode: x })} /></Field>
        </div>
        <Field label="Descripción" wide><Input value={v.description ?? ''} onChange={(x) => setV({ ...v, description: x })} maxLength={300} /></Field>
        <Alert>La plantilla define las cuentas y el lado de cada monto (total, subtotal, descuento, impuesto, otros cargos; exento y gravado son opcionales). Si los importes no cuadran, el asiento se rechaza.</Alert>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy} disabled={!customer || v.total <= 0 || !v.currencyId || !v.number.trim()}>Registrar</Button></div>
      </Form>
    </Modal>
  );
}

function ApplicationsTable({ rows, onChanged }: { rows: ReceivableApplicationDto[]; onChanged?: () => void }) {
  const { run, busy } = useAction();
  return (
    <Table rows={rows} rowKey={(a) => a.id} empty="Sin aplicaciones." columns={[
      { key: 'f', title: 'Fecha', render: (a) => fmtDate(a.date) },
      { key: 'd', title: 'Documento', render: (a) => `${a.documentType} ${a.documentNumber}` },
      { key: 'o', title: 'Origen', render: (a) => `${a.sourceKind === 'PAGO' ? 'Cobro' : 'NC'} ${a.sourceNumber}` },
      money<ReceivableApplicationDto>('m', 'Monto', (a) => a.amount),
      { key: 'e', title: 'Estado', render: (a) => <Badge tone={statusTone(a.status)}>{AR_STATUS[a.status] ?? a.status}</Badge> },
      {
        key: 'x', title: '', render: (a) => (onChanged && a.sourceKind === 'NC' && a.status === 'APL' ? (
          <Button kind="link" busy={busy} onClick={() => run((reason) => receivablesApi.reverseApplication(a.id, reason ?? ''), {
            confirm: `¿Revertir la aplicación de la nota ${a.sourceNumber} a ${a.documentType} ${a.documentNumber}? Se restituyen ambos saldos.`, requireReason: true, success: 'Aplicación revertida.',
          }).then(onChanged)}>Revertir</Button>
        ) : null),
      },
    ]} />
  );
}

function DocumentDetail({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void }) {
  const doc = useAsync(() => receivablesApi.document(id), [id]);
  const { run, busy } = useAction();
  const [applying, setApplying] = useState(false);
  const reload = () => { doc.reload(); onChanged(); };
  return (
    <Modal title="Documento de CxC" onClose={onClose} size="lg">
      <Loadable state={doc}>
        {(d) => (
          <>
            <div className="acc-grid-form">
              <Field label="Documento">{DOC_TYPES[d.type]} {d.number}</Field>
              <Field label="Cliente">{d.customerCode} · {d.customerName}</Field>
              <Field label="Fecha / vence">{fmtDate(d.date)} / {fmtDate(d.dueDate)}</Field>
              <Field label="Estado"><Badge tone={statusTone(d.status)}>{AR_STATUS[d.status] ?? d.status}</Badge></Field>
              <Field label="Total">{fmtMoney(d.total)}</Field>
              <Field label="Saldo">{fmtMoney(d.balance)}</Field>
              <Field label="Tipo de cambio">{d.exchangeRate}</Field>
              <Field label="Saldo en moneda base">{fmtMoney(d.balanceBase)}</Field>
              <Field label="Asiento">{d.entryCode ?? ''}{d.cancellationEntryCode ? ` · reverso ${d.cancellationEntryCode}` : ''}</Field>
              {d.referenceDocumentNumber && <Field label="Referencia">{d.referenceDocumentNumber}</Field>}
              <Field label="Origen">{d.origin}</Field>
            </div>
            {d.description && <p>{d.description}</p>}
            {d.cancellationReason && <Alert kind="warn">Anulado: {d.cancellationReason}</Alert>}
            <h4>Aplicaciones</h4>
            <ApplicationsTable rows={d.applications} onChanged={reload} />
            <div className="acc-actions">
              {d.type === 'NC' && d.status === 'PEN' && d.balance > 0 && <Button onClick={() => setApplying(true)}>Aplicar a documentos</Button>}
              {d.status !== 'ANU' && (
                <Button kind="danger" busy={busy} onClick={() => run((reason) => receivablesApi.cancelDocument(d.id, reason ?? ''), {
                  confirm: `¿Anular ${DOC_TYPES[d.type].toLowerCase()} ${d.number}? Se anula su asiento con un reverso.`, danger: true, requireReason: true, success: 'Documento anulado.',
                }).then(reload)}>Anular</Button>
              )}
              <Button onClick={onClose}>Cerrar</Button>
            </div>
            {applying && <ApplyCreditNoteForm note={d} onClose={() => setApplying(false)} onSaved={() => { setApplying(false); reload(); }} />}
          </>
        )}
      </Loadable>
    </Modal>
  );
}

/** Tabla de documentos pendientes del cliente con el monto a aplicar a cada uno. */
function AllocationTable({ customerId, currencyId, alloc, setAlloc }: { customerId: number; currencyId: number; alloc: Record<number, number>; setAlloc: (a: Record<number, number>) => void }) {
  const docs = useAsync(() => receivablesApi.documents({ customerId, onlyPending: true, pageSize: 200 }), [customerId]);
  return (
    <Loadable state={docs}>
      {(r) => {
        const rows = r.items.filter((d) => d.type !== 'NC' && d.currencyId === currencyId);
        return (
          <Table rows={rows} rowKey={(d) => d.id} empty="No hay facturas ni notas de débito pendientes en esta moneda." columns={[
            { key: 'd', title: 'Documento', render: (d) => `${d.type} ${d.number}` },
            { key: 'f', title: 'Fecha', render: (d) => fmtDate(d.date) },
            { key: 'v', title: 'Vence', render: (d) => fmtDate(d.dueDate) },
            money<ReceivableDocumentDto>('s', 'Saldo', (d) => d.balance),
            { key: 'a', title: 'Aplicar', render: (d) => <NumberInput value={alloc[d.id] ?? null} min={0} max={d.balance} onChange={(x) => setAlloc({ ...alloc, [d.id]: Math.min(x, d.balance) })} /> },
            { key: 'x', title: '', render: (d) => <Button kind="link" onClick={() => setAlloc({ ...alloc, [d.id]: d.balance })}>Todo</Button> },
          ]} />
        );
      }}
    </Loadable>
  );
}

const toAllocations = (alloc: Record<number, number>) => Object.entries(alloc).filter(([, a]) => a > 0).map(([id, a]) => ({ documentId: Number(id), amount: round2(a) }));

function ApplyCreditNoteForm({ note, onClose, onSaved }: { note: ReceivableDocumentDto; onClose: () => void; onSaved: () => void }) {
  const [alloc, setAlloc] = useState<Record<number, number>>({});
  const [date, setDate] = useState(todayIso());
  const { run, busy } = useAction();
  const total = round2(Object.values(alloc).reduce((s, x) => s + (x || 0), 0));
  return (
    <Modal title={`Aplicar nota de crédito ${note.number}`} onClose={onClose} size="lg">
      <Form onSubmit={() => run(async () => { await receivablesApi.applyCreditNote(note.id, toAllocations(alloc), date); onSaved(); }, { success: 'Nota aplicada.' })}>
        <Field label="Fecha de aplicación"><Input type="date" value={date} onChange={setDate} /></Field>
        <AllocationTable customerId={note.customerId} currencyId={note.currencyId} alloc={alloc} setAlloc={setAlloc} />
        <Alert>Aplicar una nota no genera asiento: el asiento se registró al crear la nota. Disponible: {fmtMoney(note.balance)}.</Alert>
        <div className="acc-actions"><strong>Total: {fmtMoney(total)}</strong><Button onClick={onClose}>Cancelar</Button>
          <Button kind="primary" type="submit" busy={busy} disabled={total <= 0 || total > note.balance}>Aplicar</Button></div>
      </Form>
    </Modal>
  );
}

// ================================================================ Cobros

function PaymentsTab() {
  const [f, setF] = useState({ number: '', status: '', page: 1 });
  const list = useAsync(() => receivablesApi.payments({ number: f.number || undefined, status: f.status || undefined, page: f.page, pageSize: 50 }), [f]);
  const currencies = useCurrencies();
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState<ReceivablePaymentDto | null>(null);
  return (
    <>
      <Toolbar>
        <Input value={f.number} onChange={(v) => setF({ ...f, number: v, page: 1 })} placeholder="Número de recibo" />
        <Select value={f.status} onChange={(v) => setF({ ...f, status: v, page: 1 })} placeholder="Todos" options={['APL', 'ANU'].map((value) => ({ value, label: AR_STATUS[value] }))} />
        <Button kind="primary" onClick={() => setCreating(true)}>Registrar cobro</Button>
      </Toolbar>
      <Loadable state={list}>
        {(r) => (
          <>
            <Table rows={r.items} rowKey={(p) => p.id} onRowClick={(p) => setDetail(p)} columns={[
              { key: 'n', title: 'Recibo', render: (p) => p.number },
              { key: 'c', title: 'Cliente', render: (p) => `${p.customerCode}${p.customerName ? ` · ${p.customerName}` : ''}` },
              { key: 'f', title: 'Fecha', render: (p) => fmtDate(p.date) },
              { key: 'fp', title: 'Forma', render: (p) => `${p.paymentMethod}${p.isCash ? ' (efectivo)' : ''}` },
              { key: 'm', title: 'Moneda', render: (p) => currencyLabel(currencies.data, p.currencyId) },
              money<ReceivablePaymentDto>('a', 'Monto', (p) => p.amount),
              money<ReceivablePaymentDto>('ne', 'Neto', (p) => p.net),
              { key: 'e', title: 'Estado', render: (p) => <Badge tone={statusTone(p.status)}>{AR_STATUS[p.status] ?? p.status}</Badge> },
              { key: 'o', title: 'Origen', render: (p) => p.origin },
              { key: 'as', title: 'Asiento', render: (p) => p.entryCode ?? '' },
            ]} />
            <Pager page={r.page} totalPages={r.totalPages} total={r.total} onPage={(p) => setF({ ...f, page: p })} />
          </>
        )}
      </Loadable>
      {creating && <PaymentForm onClose={() => setCreating(false)} onSaved={() => { setCreating(false); list.reload(); }} />}
      {detail && <PaymentDetail id={detail.id} onClose={() => setDetail(null)} onChanged={list.reload} />}
    </>
  );
}

function PaymentDetail({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void }) {
  const p = useAsync(() => receivablesApi.payment(id), [id]);
  const { run, busy } = useAction();
  return (
    <Modal title="Cobro" onClose={onClose} size="lg">
      <Loadable state={p}>
        {(x) => (
          <>
            <div className="acc-grid-form">
              <Field label="Recibo">{x.number}</Field>
              <Field label="Cliente">{x.customerCode} · {x.customerName}</Field>
              <Field label="Fecha">{fmtDate(x.date)}</Field>
              <Field label="Forma de pago">{x.paymentMethod}{x.isCash ? ' (efectivo)' : ''}</Field>
              <Field label="Monto aplicado">{fmtMoney(x.amount)}</Field>
              <Field label="Comisión / retenciones">{fmtMoney(x.commission)} / {fmtMoney(x.withheldTax)} / {fmtMoney(x.incomeTaxWithholding)}</Field>
              <Field label="Neto recibido">{fmtMoney(x.net)}</Field>
              <Field label="Tipo de cambio">{x.exchangeRate}</Field>
              {x.bankAccountCode && <Field label="Cuenta bancaria">{x.bankAccountCode}</Field>}
              {x.cashRegisterId && <Field label="Caja / apertura">{x.cashRegisterId} / {x.openingId ?? '—'}</Field>}
              <Field label="Asiento">{x.entryCode ?? ''}{x.cancellationEntryCode ? ` · reverso ${x.cancellationEntryCode}` : ''}</Field>
              <Field label="Estado"><Badge tone={statusTone(x.status)}>{AR_STATUS[x.status] ?? x.status}</Badge></Field>
            </div>
            {x.cancellationReason && <Alert kind="warn">Anulado: {x.cancellationReason}</Alert>}
            <h4>Documentos aplicados</h4>
            <ApplicationsTable rows={x.applications} />
            <div className="acc-actions">
              {x.status === 'APL' && (
                <Button kind="danger" busy={busy} onClick={() => run((reason) => receivablesApi.cancelPayment(x.id, reason ?? ''), {
                  confirm: `¿Anular el cobro ${x.number}? Se restituye el saldo de los documentos, se anula el movimiento de caja (si aplica) y se reversa el asiento.`,
                  danger: true, requireReason: true, success: 'Cobro anulado.',
                }).then(() => { p.reload(); onChanged(); })}>Anular</Button>
              )}
              <Button onClick={onClose}>Cerrar</Button>
            </div>
          </>
        )}
      </Loadable>
    </Modal>
  );
}

function PaymentForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [customer, setCustomer] = useState<CustomerDto | null>(null);
  const registers = useAsync(() => cashApi.registers(true).catch(() => []), []);
  const [h, setH] = useState({ number: '', date: todayIso(), currencyId: 0, exchangeRate: null as number | null, paymentMethod: 'TRANSFERENCIA', isCash: false, bankAccountCode: '', cashRegisterId: 0, commission: 0, withheldTax: 0, incomeTaxWithholding: 0, reference: '', description: '', templateCode: null as number | null });
  const [alloc, setAlloc] = useState<Record<number, number>>({});
  const { run, busy } = useAction();
  const total = round2(Object.values(alloc).reduce((s, x) => s + (x || 0), 0));
  const net = round2(total - h.commission - h.withheldTax - h.incomeTaxWithholding);
  return (
    <Modal title="Registrar cobro" onClose={onClose} size="lg">
      <Form onSubmit={() => run(async () => {
        await receivablesApi.createPayment({
          customerCode: customer!.code, number: h.number.trim(), date: h.date, currencyId: h.currencyId, exchangeRate: h.exchangeRate, paymentMethod: h.paymentMethod.trim(),
          isCash: h.isCash, bankAccountCode: h.bankAccountCode || null, cashRegisterId: h.cashRegisterId || null, commission: h.commission, withheldTax: h.withheldTax,
          incomeTaxWithholding: h.incomeTaxWithholding, reference: h.reference.trim() || null, description: h.description.trim() || null, templateCode: h.templateCode,
          allocations: toAllocations(alloc),
        });
        onSaved();
      }, { success: 'Cobro aplicado con su asiento.' })}>
        <Field label="Cliente" wide><CustomerSearch value={customer} onChange={(c) => { setCustomer(c); setAlloc({}); }} /></Field>
        <div className="acc-grid-form">
          <Field label="Número de recibo"><Input value={h.number} onChange={(x) => setH({ ...h, number: x })} maxLength={50} required /></Field>
          <Field label="Fecha"><Input type="date" value={h.date} onChange={(x) => setH({ ...h, date: x })} required /></Field>
          <Field label="Moneda"><CurrencySelect value={h.currencyId || null} onChange={(x) => { setH({ ...h, currencyId: x }); setAlloc({}); }} /></Field>
          <Field label="Tipo de cambio" hint="Vacío = tipo de cambio del día"><NumberInput step="0.000001" value={h.exchangeRate} onChange={(x) => setH({ ...h, exchangeRate: x || null })} /></Field>
          <Field label="Forma de pago"><Input value={h.paymentMethod} onChange={(x) => setH({ ...h, paymentMethod: x })} maxLength={30} list="acc-payment-methods" />
            <datalist id="acc-payment-methods">{['EFECTIVO', 'TARJETA', 'TRANSFERENCIA', 'SINPE', 'CHEQUE'].map((m) => <option key={m} value={m} />)}</datalist></Field>
          <Field label="Efectivo"><Check checked={h.isCash} onChange={(x) => setH({ ...h, isCash: x })} label="Entra en el arqueo de caja" /></Field>
          <Field label="Cuenta bancaria (depósito)"><BankAccountSelect value={h.bankAccountCode} onChange={(x) => setH({ ...h, bankAccountCode: x })} placeholder="(según plantilla)" /></Field>
          <Field label="Caja"><Select value={h.cashRegisterId || ''} onChange={(x) => setH({ ...h, cashRegisterId: x || 0 })} placeholder="(ninguna)"
            options={(registers.data ?? []).map((c) => ({ value: c.id, label: `${c.name}${c.isOpen ? '' : ' (cerrada)'}` }))} /></Field>
          <Field label="Comisión"><NumberInput value={h.commission} onChange={(x) => setH({ ...h, commission: x })} min={0} /></Field>
          <Field label="Impuesto retenido"><NumberInput value={h.withheldTax} onChange={(x) => setH({ ...h, withheldTax: x })} min={0} /></Field>
          <Field label="Retención de renta"><NumberInput value={h.incomeTaxWithholding} onChange={(x) => setH({ ...h, incomeTaxWithholding: x })} min={0} /></Field>
          <Field label="Referencia"><Input value={h.reference} onChange={(x) => setH({ ...h, reference: x })} maxLength={100} /></Field>
          <Field label="Plantilla"><TemplateSelect moduleCode="CxC" value={h.templateCode} onChange={(x) => setH({ ...h, templateCode: x })} /></Field>
        </div>
        <Field label="Descripción" wide><Input value={h.description} onChange={(x) => setH({ ...h, description: x })} maxLength={300} /></Field>
        {customer && h.currencyId > 0 && <AllocationTable customerId={customer.id} currencyId={h.currencyId} alloc={alloc} setAlloc={setAlloc} />}
        <Alert>El cobro y los documentos deben estar en la misma moneda; el monto es la suma de lo aplicado (no se registran anticipos). En moneda extranjera la diferencia de tipo de cambio va a las líneas de diferencial cambiario (ganancia o pérdida) de la plantilla.</Alert>
        <div className="acc-actions"><strong>Aplicado: {fmtMoney(total)} · Neto: {fmtMoney(net)}</strong><Button onClick={onClose}>Cancelar</Button>
          <Button kind="primary" type="submit" busy={busy} disabled={!customer || total <= 0 || net <= 0 || !h.currencyId || !h.number.trim() || !h.paymentMethod.trim()}>Registrar cobro</Button></div>
      </Form>
    </Modal>
  );
}

// ================================================================ Clientes

const emptyCustomer: SaveCustomerDto = { code: '', name: '', idType: '', idNumber: '', email: '', phone: '', address: '', creditDays: 0, creditLimit: null, status: 'A' };

function CustomersTab() {
  const [f, setF] = useState({ text: '', page: 1 });
  const list = useAsync(() => receivablesApi.customers({ text: f.text || undefined, page: f.page, pageSize: 50 }), [f]);
  const [editing, setEditing] = useState<CustomerDto | 'new' | null>(null);
  return (
    <>
      <Toolbar>
        <Input value={f.text} onChange={(v) => setF({ text: v, page: 1 })} placeholder="Buscar" />
        <Button kind="primary" onClick={() => setEditing('new')}>Nuevo cliente</Button>
      </Toolbar>
      <Loadable state={list}>
        {(r) => (
          <>
            <Table rows={r.items} rowKey={(c) => c.id} onRowClick={(c) => setEditing(c)} columns={[
              { key: 'c', title: 'Código', render: (c) => c.code },
              { key: 'n', title: 'Nombre', render: (c) => c.name },
              { key: 'i', title: 'Identificación', render: (c) => c.idNumber ?? '' },
              { key: 'd', title: 'Días crédito', align: 'right', render: (c) => c.creditDays },
              money<CustomerDto>('s', 'Saldo (base)', (c) => c.balanceBase),
              { key: 'o', title: 'Doc. abiertos', align: 'right', render: (c) => c.openDocuments },
              { key: 'e', title: 'Estado', render: (c) => <Badge tone={c.status === 'A' ? 'ok' : 'neutral'}>{c.status === 'A' ? 'Activo' : 'Inactivo'}</Badge> },
            ]} />
            <Pager page={r.page} totalPages={r.totalPages} total={r.total} onPage={(p) => setF({ ...f, page: p })} />
          </>
        )}
      </Loadable>
      {editing && <CustomerForm customer={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); list.reload(); }} />}
    </>
  );
}

function CustomerForm({ customer, onClose, onSaved }: { customer: CustomerDto | null; onClose: () => void; onSaved: () => void }) {
  const country = useCountry();
  const [v, setV] = useState<SaveCustomerDto>(customer ? { code: customer.code, name: customer.name, idType: customer.idType ?? '', idNumber: customer.idNumber ?? '', email: customer.email ?? '', phone: customer.phone ?? '', address: customer.address ?? '', creditDays: customer.creditDays, creditLimit: customer.creditLimit ?? null, status: customer.status } : emptyCustomer);
  const { run, busy, fieldErrors } = useAction();
  const clean = (s?: string | null) => (s && s.trim() ? s.trim() : null);
  return (
    <Modal title={customer ? `Cliente ${customer.code}` : 'Nuevo cliente'} onClose={onClose}>
      <Form onSubmit={() => run(async () => {
        const dto = { ...v, code: v.code.trim(), name: v.name.trim(), idType: clean(v.idType), idNumber: clean(v.idNumber), email: clean(v.email), phone: clean(v.phone), address: clean(v.address) };
        if (customer) await receivablesApi.updateCustomer(customer.id, dto); else await receivablesApi.createCustomer(dto);
        onSaved();
      }, { success: 'Cliente guardado.' })}>
        <div className="acc-grid-form">
          <Field label="Código" hint="Código en el sistema origen; no se cambia después" error={fieldErrors.Code}><Input value={v.code} onChange={(x) => setV({ ...v, code: x })} maxLength={50} disabled={!!customer} required /></Field>
          <Field label="Nombre" error={fieldErrors.Name}><Input value={v.name} onChange={(x) => setV({ ...v, name: x })} maxLength={200} required /></Field>
          <Field label="Tipo de identificación"><Select value={v.idType ?? ''} onChange={(x) => setV({ ...v, idType: x })} placeholder="Elija" options={idTypeOptions([...(country?.companyIdTypes ?? []), ...(country?.personIdTypes ?? [])].filter((t, i, all) => all.findIndex((y) => y.code === t.code) === i), v.idType)} /></Field>
          <Field label="Identificación"><Input value={v.idNumber ?? ''} onChange={(x) => setV({ ...v, idNumber: x })} maxLength={50} /></Field>
          <Field label="Correo" error={fieldErrors.Email}><Input value={v.email ?? ''} onChange={(x) => setV({ ...v, email: x })} maxLength={150} /></Field>
          <Field label="Teléfono"><Input value={v.phone ?? ''} onChange={(x) => setV({ ...v, phone: x })} maxLength={50} /></Field>
          <Field label="Días de crédito"><NumberInput step="1" value={v.creditDays} onChange={(x) => setV({ ...v, creditDays: Math.max(0, Math.round(x)) })} min={0} /></Field>
          <Field label="Límite de crédito" hint="Informativo"><NumberInput value={v.creditLimit} onChange={(x) => setV({ ...v, creditLimit: x || null })} min={0} /></Field>
          <Field label="Estado"><Select value={v.status} onChange={(x) => setV({ ...v, status: x })} options={[{ value: 'A', label: 'Activo' }, { value: 'I', label: 'Inactivo' }]} /></Field>
        </div>
        <Field label="Dirección" wide><Input value={v.address ?? ''} onChange={(x) => setV({ ...v, address: x })} maxLength={300} /></Field>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy} disabled={!v.code.trim() || !v.name.trim()}>Guardar</Button></div>
      </Form>
    </Modal>
  );
}

// ================================================================ Estado de cuenta

function StatementTab() {
  const [customer, setCustomer] = useState<CustomerDto | null>(null);
  return (
    <Card>
      <Field label="Cliente" wide><CustomerSearch value={customer} onChange={setCustomer} /></Field>
      {customer && <StatementView load={(from, to) => receivablesApi.statement(customer.id, from, to)} />}
    </Card>
  );
}
